/* =====================================================================
   AUTH RATE LIMITER
   =====================================================================
   Client-side protection against repeated auth failures.

   Strategy:
   - Tracks consecutive failures per session (in-memory, not persisted)
   - After FAILURE_THRESHOLD failures, enforces an exponential cooldown
   - Certain non-malicious errors (popup closed, cancelled) are excluded
   - A single success resets the counter

   This complements Firebase's server-side rate limiting; it prevents
   the UI from hammering the auth endpoints during network blips or
   accidental rapid retries.
===================================================================== */

const FAILURE_THRESHOLD = 5;
const BASE_COOLDOWN_MS = 30_000;
const BACKOFF_FACTOR = 2;
const MAX_COOLDOWN_MS = 15 * 60 * 1000; // 15 min hard cap

/**
 * Error codes that should NOT count toward the failure rate.
 * These represent user-initiated cancellations, not actual failures.
 */
const NON_BLOCKING_CODES = new Set([
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
]);

class AuthRateLimiter {
  constructor() {
    this._consecutiveFailures = 0;
    this._blockedUntil = 0;
  }

  /** Returns true if auth actions should be blocked right now. */
  isBlocked() {
    if (this._blockedUntil === 0) return false;
    if (Date.now() < this._blockedUntil) return true;
    this._reset();
    return false;
  }

  /** Milliseconds remaining in the current cooldown (0 if not blocked). */
  getRemainingMs() {
    return Math.max(0, this._blockedUntil - Date.now());
  }

  /** Record a failure. Non-blocking error codes are ignored. */
  recordFailure(error) {
    const code = String(error?.code || "");
    if (NON_BLOCKING_CODES.has(code)) return;

    this._consecutiveFailures++;

    if (this._consecutiveFailures >= FAILURE_THRESHOLD) {
      const tier = Math.floor(this._consecutiveFailures / FAILURE_THRESHOLD) - 1;
      const cooldown = Math.min(
        BASE_COOLDOWN_MS * Math.pow(BACKOFF_FACTOR, tier),
        MAX_COOLDOWN_MS
      );
      this._blockedUntil = Date.now() + cooldown;
    }
  }

  /** A successful action resets the failure counter entirely. */
  recordSuccess() {
    this._reset();
  }

  _reset() {
    this._consecutiveFailures = 0;
    this._blockedUntil = 0;
  }
}

export const authRateLimiter = new AuthRateLimiter();
